'use client';
import { useIntl } from '@capsicum/common';
import dynamic from 'next/dynamic';
import { useCallback, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { useDrawer } from '../../../hooks/drawer/useDrawer';
import { useRTL } from '../../../hooks/helpers/useRTL';
import { type MenuItemProps } from '../../atoms/menu-item/MenuItem';
import { DrawerWrapper } from '../../molecules/drawer/DrawerWrapper';
import { Snackbar } from '../snackbar/Snackbar';

import { type DrawerProfileProps } from './DrawerTopContent/DrawerProfile';

export { type DrawerProfileProps } from './DrawerTopContent/DrawerProfile';

export type DrawerProps = {
	appVersion?: string;
	items: Array<MenuItemProps>;
	profileProps: DrawerProfileProps;
};

const LazyDrawerContent = dynamic(() => import('./DrawerEntryPoint').then(module => module.DrawerContent));
const LazyDrawerTopContent = dynamic(() => import('./DrawerEntryPoint').then(module => module.DrawerTopContent));
const LazyDrawerFooter = dynamic(() => import('./DrawerEntryPoint').then(module => module.DrawerFooter));

export const Drawer = ({ appVersion, items, profileProps }: DrawerProps) => {
	const { isRTL } = useRTL();
	const { colors } = useTheme();
	const { closeDrawer, isDrawerOpen } = useDrawer();
	const [showSnackbar, setShowSnackbar] = useState(false);
	const { $t } = useIntl();

	const onCopyCallback = useCallback(() => {
		setShowSnackbar(true);
		setTimeout(() => setShowSnackbar(false), 3000);
	}, [setShowSnackbar]);

	return (
		<>
			<DrawerWrapper
				footerChildren={appVersion ? <LazyDrawerFooter appVersion={appVersion} /> : undefined}
				headerChildren={
					<LazyDrawerTopContent
						handleClose={closeDrawer}
						iconColor={colors.icons.main.enabled}
						{...profileProps}
						onCopyCallback={onCopyCallback}
					/>
				}
				isRTL={isRTL}
				showDrawer={isDrawerOpen}
				testID="app-main-drawer"
				onCloseDrawer={() => closeDrawer()}
			>
				<S.BottomContent>
					<LazyDrawerContent items={items} />
				</S.BottomContent>
			</DrawerWrapper>
			{showSnackbar && (
				<Snackbar
					description={$t('profile.copieWalletAlertMsg')}
					style={{ bottom: 40, left: 20, maxWidth: 300, position: 'absolute', zIndex: 1002 }}
					testID="drawer-copytext-snackbar"
					onActionActivate={() => setShowSnackbar(false)}
					onActivate={() => setShowSnackbar(false)}
					onRightIconActivate={() => setShowSnackbar(false)}
				/>
			)}
		</>
	);
};

namespace S {
	export const BottomContent = styled.div`
		display: flex;
	`;
}
